/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import classnames from 'classnames';
import React from 'react';

import { Button, format, KeyCodes, Node, UncontrolledPagination } from '..';
import CheckBoxButton from './checkbox';

/**
 * Table Resources.
 */
export interface ITableResources {}

/**
 * Table props.
 */
export interface IDataTableProps {
    resources: ITableResources;
    headings: (IHeadingsProperty | undefined)[];
    rows: ITableRowProps[];
    className?: string;
    editLinkText?: string;
    actionLinkText?: string;
    deleteLinkText?: string;
    viewLinkText?: string;
    editLinkTooltip?: string;
    actionLinkTooltip?: string;
    deleteLinkTooltip?: string;
    viewLinkTooltip?: string;
    enableToModify: boolean;
    showCheckBoxes?: boolean;
    isSortable?: boolean;
    showPagination?: boolean;
    paginationProperty: IPaginationProperty;
    minifyActions?: boolean;
    minifiedButtonText?: string;
    excludedColumns?: string[]; // Filled with ITableItemProps.id of columns not to show
    isSingleSelectOnly: boolean;
    businessUserSelectCheckBoxAriaLabelText?: string;
    sortByAscending?: string;
    sortByDescending?: string;
    resourcePrefix?: string;
    actions: {
        onDelete?(userData: ITableItemProps[], referenceBack?: HTMLButtonElement | null): void | undefined;
        onEdit?(userData: ITableItemProps[], referenceBack?: HTMLButtonElement | null): void | undefined;
        onView?(userData: ITableItemProps[]): void | undefined;
    };
    checkBoxCallback?(records: ITableRowProps[]): void;
    formatPrice?(price: number): string;
    tableAriaLabel?: string;
    tableTabIndex?: number;
}

/**
 * Table IPaginationProperty props.
 */
export interface IPaginationProperty {
    itemPerPage: number;
    skipCount: number;
    prevText: string;
    nextText: string;
    paginationText: string;
    url: string;
}

/**
 * Table ITableItemProps props.
 */
export interface ITableItemProps {
    id: string;
    type: TableDataType;
    value: boolean | number | string;
    className?: string;
}

/**
 * Table IButtonReferences props.
 */
export interface IButtonReferences {
    edit?: React.RefObject<HTMLButtonElement>;
    delete?: React.RefObject<HTMLButtonElement>;
}

/**
 * Table ITableRowProps props.
 */
export interface ITableRowProps {
    row: ITableItemProps[];
    isSelected: boolean;
    buttonReferences?: IButtonReferences;
}

/**
 * Table IDataTableCells props.
 */
interface IDataTableCells {
    header: boolean;
    className: string;
    content: string | number | React.ReactNode;
    cellIndex: number;
    scope: 'col' | 'row';
    colSpan?: number;
    records?: ITableRowProps;
}

/**
 * Table IHeadingsProperty props.
 */
export interface IHeadingsProperty {
    name: string;
    sortable: boolean;
    ariaLabel?: string;
}

/**
 * Table ITableState props.
 */
export interface ITableState {
    sortingKey: string;
    ascendingOrder: boolean;
    showActions: boolean;
    selectedIndex: string;
}

/**
 * Table TableDataType props.
 */
export enum TableDataType {
    Number = 'Number',
    Text = 'Text',
    Price = 'Price'
}

export default class Table extends React.PureComponent<IDataTableProps, ITableState> {
    public componentClassName: string = 'ms-table';

    private readonly headingPrefix: string = 'table';

    private readonly capitalizeHeadingPrefix: string = 'Table';

    constructor(props: IDataTableProps) {
        super(props);
        this.state = {
            ascendingOrder: true,
            sortingKey: (props.headings[0] && props.headings[0].name) || '',
            showActions: props.minifyActions !== undefined ? !props.minifyActions : true,
            selectedIndex: ''
        };
    }

    public render(): JSX.Element {
        const { className, headings, rows, isSortable, showPagination, paginationProperty, tableAriaLabel, tableTabIndex } = this.props;
        const { sortingKey, ascendingOrder } = this.state;

        const classname = classnames(this.componentClassName, className);
        let tableRows = isSortable && sortingKey ? this._sortTableRow(rows, sortingKey, ascendingOrder) : rows;
        let paginationLinks = null;

        if (showPagination) {
            const { skipCount, itemPerPage, nextText, prevText, paginationText, url } = paginationProperty;

            paginationLinks = (
                <UncontrolledPagination
                    className="ms-table-business-org__pagination"
                    role="navigation"
                    aria-label={paginationText}
                    url={url}
                    qsp="skip"
                    items={tableRows.length}
                    itemsPerPage={itemPerPage}
                    startingItem={skipCount}
                    previousText={this._renderPrev(prevText)}
                    nextText={this._renderNext(nextText)}
                    previousAriaLabel={prevText}
                    nextAriaLabel={nextText}
                />
            );

            tableRows = this._getCurrentPageData(tableRows, skipCount, skipCount + itemPerPage);
        }

        const tableBody = this._renderRow(tableRows);
        const tableHead = this._renderHeadingRow(headings);

        return (
            <div className={`${this.componentClassName}__container`}>
                <table className={classname} aria-label={tableAriaLabel} tabIndex={tableTabIndex}>
                    <thead>{tableHead}</thead>
                    <tbody>{tableBody}</tbody>
                </table>
                {paginationLinks}
            </div>
        );
    }

    /**
     * Set sorting key.
     * @param event -The mouse event.
     */
    private readonly _selectSortingKey = (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent) => {
        const target = event.target as HTMLElement;
        const columnName = target.getAttribute('data-sort-name') ? (target.getAttribute('data-sort-name') as string) : '';
        const { sortingKey, ascendingOrder } = this.state;
        let newOrder = true;

        if (sortingKey === columnName) {
            newOrder = !ascendingOrder;
        }

        this.setState({
            sortingKey: columnName,
            ascendingOrder: newOrder,
            showActions: false
        });
    };

    /**
     * Checkbox change.
     * @param rows -Table rows.
     * @param checkBoxCallback -Checkboxcallback function.
     * @param index -Index value for row.
     * @returns Checkbox rows.
     */
    private readonly _onCheckBoxChangeHandler = (
        rows: ITableRowProps[],
        checkBoxCallback: (records: ITableRowProps[]) => void,
        index?: number
    ) => () => {
        if (ObjectExtensions.isNumber(index)) {
            rows[index].isSelected = !rows[index].isSelected;
        }
        checkBoxCallback(rows);
    };

    private readonly _renderHeadingRow = (columnData: (IHeadingsProperty | undefined)[]) => {
        const {
            resources,
            showCheckBoxes,
            enableToModify,
            minifyActions,
            rows,
            actionLinkText,
            isSortable,
            checkBoxCallback,
            resourcePrefix,
            isSingleSelectOnly,
            businessUserSelectCheckBoxAriaLabelText,
            sortByAscending,
            sortByDescending
        } = this.props;
        const { sortingKey, ascendingOrder } = this.state;

        const sortingClassName = ascendingOrder ? 'asc' : 'dsc';

        const sortingValue = ascendingOrder ? sortByAscending : sortByDescending;

        const columns = columnData.map((data, cellIndex) => {
            if (!data) {
                return;
            }
            const counterAddition = 1;
            const updatedCellIndex = cellIndex + counterAddition;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- unsafe assignment.
            const heading =
                resources[`${this.headingPrefix}${data.name}HeadingText`] ||
                resources[`${resourcePrefix!}${this.capitalizeHeadingPrefix}${data.name}HeadingText`] ||
                resources[`${data.name}HeadingText`] ||
                data.name;

            const columnSortValue = sortingKey === data.name && sortingValue !== undefined ? sortingValue : '';

            const columnAriaLabel = data.ariaLabel ? `${format(data.ariaLabel, heading)} ${columnSortValue}` : '';

            const content = (isSortable && data.sortable && (
                <th
                    data-sort-name={data.name}
                    onClick={this._selectSortingKey}
                    onKeyUp={this._handleKeyPressPrev}
                    className={classnames(`${this.componentClassName}__heading-row-data`)}
                    key={`heading-${updatedCellIndex}`}
                    scope="col"
                    role="columnheader"
                    tabIndex={0}
                >
                    <div
                        className={classnames(`${this.componentClassName}__heading-sorting`, sortingKey === data.name && sortingClassName)}
                        aria-label={data.ariaLabel && columnAriaLabel.trim()}
                        data-sort-name={`${data.name}`}
                        aria-live="polite"
                    >
                        {heading}
                    </div>
                </th>
            )) || (
                <th scope="col" role="columnheader" tabIndex={0}>
                    {heading}
                </th>
            );

            return content;
        });

        const radioCheckBox = showCheckBoxes && checkBoxCallback && !isSingleSelectOnly && (
            <CheckBoxButton
                onChange={this._onCheckBoxChangeHandler(rows, checkBoxCallback)}
                name="All"
                ariaLabel={businessUserSelectCheckBoxAriaLabelText}
            />
        );

        const actionContent = (
            <div
                className={classnames(`${this.componentClassName}__heading-action`)}
                aria-label={minifyActions ? '' : actionLinkText}
                aria-live="polite"
            >
                {minifyActions ? '' : actionLinkText}
            </div>
        );

        return (
            <tr className={`${this.componentClassName}__heading-row`}>
                {showCheckBoxes &&
                    this.tableCell({
                        header: true,
                        className: `${this.componentClassName}__heading-row-data__checkbox checkbox`,
                        content: radioCheckBox,
                        cellIndex: -1,
                        scope: 'col'
                    })}
                {columns}
                {enableToModify &&
                    this.tableCell({
                        header: true,
                        className: `${this.componentClassName}__heading-row-data action-links`,
                        content: actionContent,
                        cellIndex: 1,
                        scope: 'col'
                    })}
            </tr>
        );
    };

    private readonly _renderRow = (rows: ITableRowProps[]) => {
        const {
            enableToModify,
            showCheckBoxes,
            excludedColumns,
            actions,
            checkBoxCallback,
            formatPrice,
            isSingleSelectOnly,
            businessUserSelectCheckBoxAriaLabelText
        } = this.props;

        const tableRows = rows.map((rowsData, index) => {
            let className = index % 2 ? `${this.componentClassName}__row even-row` : `${this.componentClassName}__row odd-row`;
            let showActions = false;

            const additionalNameAriaLabel = rowsData.row[0].value.toString();
            const columnData = rowsData.row.map(cell => {
                if (excludedColumns && excludedColumns.includes(cell.id)) {
                    return;
                }

                // Numbers/price data types will always be right-aligned per design standards
                const extraClass = cell.type === TableDataType.Number || cell.type === TableDataType.Price ? 'num-type' : '';
                const cellContent = cell.type === TableDataType.Price && formatPrice ? formatPrice(cell.value as number) : cell.value;
                const columnSpecificClass = cell.className || '';
                return this.tableCell({
                    header: false,
                    className: classnames(`${this.componentClassName}__row-data`, extraClass, columnSpecificClass),
                    content: cellContent,
                    cellIndex: index,
                    scope: 'row'
                });
            });

            const radioCheckBox = showCheckBoxes && checkBoxCallback && (
                <CheckBoxButton
                    onChange={this._onCheckBoxChangeHandler(rows, checkBoxCallback, index)}
                    name="actionOptions"
                    value={`checkbox-${index}`}
                    isSingleSelectOnly={isSingleSelectOnly}
                    ariaLabel={`${businessUserSelectCheckBoxAriaLabelText!} ${additionalNameAriaLabel}`}
                />
            );
            const rowKey = `row-${index}`;

            if (rowKey === this.state.selectedIndex && this.state.showActions) {
                className += ' selected';
                showActions = true;
            }

            const actionButtons = actions && this._renderActions(rowsData.row, showActions, rowKey, rowsData.buttonReferences);

            return (
                <tr key={rowKey} className={className}>
                    {showCheckBoxes &&
                        this.tableCell({
                            header: false,
                            className: `${this.componentClassName}__row-data`,
                            content: radioCheckBox,
                            cellIndex: index,
                            scope: 'row',
                            records: rowsData
                        })}
                    {columnData}
                    {enableToModify &&
                        this.tableCell({
                            header: false,
                            className: `${this.componentClassName}__row-data`,
                            content: actionButtons,
                            cellIndex: index,
                            scope: 'row',
                            records: rowsData
                        })}
                </tr>
            );
        });

        return tableRows;
    };

    private readonly _renderActions = (
        data: ITableItemProps[],
        showActions: boolean,
        key: string,
        buttonReference?: IButtonReferences
    ): React.ReactNode => {
        const {
            editLinkText,
            deleteLinkText,
            viewLinkText,
            editLinkTooltip,
            deleteLinkTooltip,
            viewLinkTooltip,
            actionLinkTooltip,
            minifyActions,
            minifiedButtonText,
            actionLinkText,
            actions: { onEdit, onDelete, onView }
        } = this.props;
        const handleClickEdit =
            onEdit !== undefined
                ? () => {
                      onEdit(data, buttonReference?.edit?.current);
                  }
                : undefined;
        const handleClickDelete =
            onDelete !== undefined
                ? () => {
                      onDelete(data, buttonReference?.delete?.current);
                  }
                : undefined;
        const handleClickView =
            onView !== undefined
                ? () => {
                      onView(data);
                  }
                : undefined;

        const actionButtons = (
            <Node className={`${this.componentClassName}__row-links`}>
                {handleClickView && (
                    <Button className={`${this.componentClassName}__row-links-view`} aria-label={viewLinkTooltip} onClick={handleClickView}>
                        {viewLinkText}
                    </Button>
                )}
                {handleClickEdit && (
                    <Button
                        innerRef={buttonReference?.edit}
                        className={`${this.componentClassName}__row-links-edit`}
                        aria-label={editLinkTooltip}
                        onClick={handleClickEdit}
                    >
                        {editLinkText}
                    </Button>
                )}
                {handleClickDelete && (
                    <Button
                        innerRef={buttonReference?.delete}
                        className={`${this.componentClassName}__row-links-delete`}
                        aria-label={deleteLinkTooltip}
                        onClick={handleClickDelete}
                    >
                        {deleteLinkText}
                    </Button>
                )}
            </Node>
        );

        if (minifyActions) {
            return (
                <Node className={`${this.componentClassName}__row-links-minified`}>
                    <Button
                        className={`${this.componentClassName}__row-links-toggle`}
                        aria-label={actionLinkText}
                        data-type={key}
                        title={actionLinkTooltip}
                        onClick={this._toggleActions}
                        aria-expanded={this.state.showActions}
                    >
                        {minifiedButtonText ? minifiedButtonText : ''}
                    </Button>
                    {showActions && actionButtons}
                </Node>
            );
        }
        return actionButtons;
    };

    private readonly _toggleActions = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const target = event && (event.target as HTMLElement);
        const newKey = target && target.getAttribute('data-type') ? (target.getAttribute('data-type') as string) : '';
        this.setState({
            showActions: !this.state.showActions,
            selectedIndex: newKey
        });
    };

    private readonly _sortTableRow = (table: ITableRowProps[], sortingkey: string, ascendingOrder: boolean): ITableRowProps[] => {
        return table.sort((userOne: ITableRowProps, userTwo: ITableRowProps) => {
            const userOneData = userOne.row.find(cell => {
                return cell.id === sortingkey;
            });
            const userTwoData = userTwo.row.find(cell => {
                return cell.id === sortingkey;
            });

            if (!userOneData || !userTwoData) {
                return -1;
            }

            if (typeof userOneData.value === 'number' && typeof userTwoData.value === 'number') {
                return ascendingOrder ? userOneData.value - userTwoData.value : userTwoData.value - userOneData.value;
            }

            const cleanFirst =
                (userOneData.value as string) &&
                userOneData.value
                    .toString()
                    .toLowerCase()
                    .trim();
            const cleanSecond =
                (userOneData.value as string) &&
                userTwoData.value
                    .toString()
                    .toLowerCase()
                    .trim();

            if (!cleanFirst || !cleanSecond) {
                return -1;
            }
            if (ascendingOrder) {
                return cleanFirst < cleanSecond ? -1 : 1;
            }
            return cleanFirst > cleanSecond ? -1 : 1;
        });
    };

    private readonly _getCurrentPageData = (tableRows: ITableRowProps[], start: number, end: number): ITableRowProps[] => {
        if (start >= end) {
            return tableRows;
        }

        const result = [];
        for (let i = start; i < end; i++) {
            tableRows[i] && result.push(tableRows[i]);
        }

        return result;
    };

    private readonly tableCell = (props: IDataTableCells) => {
        const { className, header, cellIndex, scope, colSpan, content } = props;
        const hiddenTabIndex = -1;
        const initialTabIndex = 0;
        return header ? (
            <th
                className={className}
                key={`heading-${cellIndex}`}
                scope={scope}
                colSpan={colSpan}
                tabIndex={cellIndex > initialTabIndex ? initialTabIndex : hiddenTabIndex}
            >
                {content}
            </th>
        ) : (
            <td className={className} scope={scope} colSpan={colSpan}>
                {content}
            </td>
        );
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className="msc-pagination__prev">
                <span className="ms-table-business-org__pagination-left" aria-hidden="true" />
                <span className="prev-text">{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className="msc-pagination__next">
                <span className="next-text">{paginationNextText}</span>
                <span className="ms-table-business-org__pagination-right" aria-hidden="true" />
            </div>
        );
    };

    /**
     * Calls method display result.
     * @param event - Keyboard event.
     */
    private readonly _handleKeyPressPrev = (event: React.KeyboardEvent): void => {
        if (event.keyCode === KeyCodes.Enter || event.keyCode === KeyCodes.Space) {
            this._selectSortingKey(event);
        }
    };
}
